/**
 * Google Analytics 4 Event Tracking Utility
 * Measurement ID: G-6E0LB2NNPW
 * 
 * CONVERSION EVENTS (Configure in GA4 Admin > Events > Mark as Conversion):
 * - sign_up
 * - purchase
 * - begin_checkout
 * - generate_lead (waitlist)
 */

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - Name of the event
 * @param {object} parameters - Event parameters
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, parameters);
    console.log(`[GA4] Event tracked: ${eventName}`, parameters);
  }
};

/**
 * Track page view (for SPA navigation)
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (isGtagAvailable()) {
    window.gtag('config', 'G-6E0LB2NNPW', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

/**
 * Set user properties for analytics
 * @param {object} userProperties - User properties to set
 */
export const setUserProperties = (userProperties) => {
  if (isGtagAvailable()) {
    window.gtag('set', 'user_properties', userProperties);
  }
};

/**
 * Identify user (for logged in users)
 * @param {string} userId - User ID
 */
export const identifyUser = (userId) => {
  if (isGtagAvailable()) {
    window.gtag('config', 'G-6E0LB2NNPW', {
      user_id: userId,
    });
  }
};

// ==================== CONVERSION EVENTS (Mark as Conversions in GA4) ====================

/**
 * Track sign up - CONVERSION EVENT
 * Mark as conversion in GA4: Admin > Events > sign_up > Mark as conversion
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', { 
    method,
    event_category: 'engagement',
    event_label: `Sign up via ${method}`
  });
};

/**
 * Track login
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', { 
    method,
    event_category: 'engagement' 
  });
};

export const trackLogout = () => {
  trackEvent('logout', { event_category: 'engagement' });
};

// ==================== E-COMMERCE CONVERSION EVENTS ====================

/**
 * Track when user views a pricing plan - helps understand interest
 */
export const trackViewItem = (planId, planName, price, currency = 'USD') => {
  trackEvent('view_item', {
    currency,
    value: price,
    items: [{
      item_id: planId,
      item_name: planName,
      price: price,
      quantity: 1,
      item_category: 'subscription'
    }]
  });
};

/**
 * Track when user clicks on a plan CTA - CONVERSION EVENT
 */
export const trackAddToCart = (planId, planName, price, currency = 'USD') => {
  trackEvent('add_to_cart', {
    currency,
    value: price,
    items: [{
      item_id: planId,
      item_name: planName,
      price: price,
      quantity: 1,
      item_category: 'subscription'
    }]
  });
};

/**
 * Track begin checkout - CONVERSION EVENT
 * Mark as conversion in GA4: Admin > Events > begin_checkout > Mark as conversion
 */
export const trackBeginCheckout = (planName, price, currency = 'USD') => {
  trackEvent('begin_checkout', {
    currency,
    value: price,
    items: [{
      item_id: planName,
      item_name: planName,
      price: price,
      quantity: 1,
      item_category: 'subscription'
    }],
    event_category: 'ecommerce',
    event_label: planName
  });
};

/**
 * Track successful purchase - CONVERSION EVENT (Most Important!)
 * Mark as conversion in GA4: Admin > Events > purchase > Mark as conversion
 */
export const trackPurchase = (planName, price, transactionId, currency = 'USD') => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    currency,
    value: price,
    tax: 0,
    shipping: 0,
    items: [{
      item_id: planName,
      item_name: planName,
      price: price,
      quantity: 1,
      item_category: 'subscription'
    }],
    event_category: 'ecommerce',
    event_label: planName
  });
  
  // Also track as a custom conversion for redundancy
  trackEvent('subscription_purchase', {
    plan_name: planName,
    value: price,
    currency,
    transaction_id: transactionId
  });
};

export const trackSubscriptionUpgrade = (fromPlan, toPlan, value = 0) => {
  trackEvent('subscription_upgrade', {
    from_plan: fromPlan,
    to_plan: toPlan,
    value,
    event_category: 'ecommerce'
  });
};

// ==================== LEAD GENERATION EVENTS ====================

/**
 * Track waitlist signup - CONVERSION EVENT (Lead Generation)
 * Mark as conversion in GA4: Admin > Events > generate_lead > Mark as conversion
 */
export const trackWaitlistSignup = (email) => {
  // Standard GA4 lead event
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 5.00, // Estimated value of a lead
    event_category: 'lead_generation',
    event_label: 'waitlist'
  });
  
  // Custom event for more detail
  trackEvent('waitlist_signup', {
    email_domain: email.split('@')[1] || 'unknown',
    event_category: 'lead_generation'
  });
};

/**
 * Track CTA clicks - helps understand user journey
 */
export const trackCTAClicked = (ctaName, location, destinationUrl = '') => {
  trackEvent('cta_clicked', {
    cta_name: ctaName,
    location,
    destination_url: destinationUrl,
    event_category: 'engagement'
  });
};

// ==================== PORTFOLIO & FEATURE EVENTS ====================

export const trackPortfolioCreated = (portfolioName, instrumentCount) => {
  trackEvent('portfolio_created', {
    portfolio_name: portfolioName,
    instrument_count: instrumentCount,
    event_category: 'feature_usage'
  });
};

export const trackPortfolioDeleted = (portfolioName) => {
  trackEvent('portfolio_deleted', {
    portfolio_name: portfolioName,
    event_category: 'feature_usage'
  });
};

export const trackBacktestRun = (riskProfile, startDate, endDate) => {
  trackEvent('backtest_run', {
    risk_profile: riskProfile,
    start_date: startDate,
    end_date: endDate,
    event_category: 'feature_usage'
  });
};

export const trackInstrumentEvaluated = (ticker, instrumentType, score = null) => {
  trackEvent('instrument_evaluated', {
    ticker,
    instrument_type: instrumentType,
    score,
    event_category: 'feature_usage'
  });
};

export const trackGuidanceGenerated = (portfolioId) => {
  trackEvent('guidance_generated', {
    portfolio_id: portfolioId,
    event_category: 'feature_usage'
  });
};

export const trackRebalanceViewed = (portfolioId) => {
  trackEvent('rebalance_viewed', {
    portfolio_id: portfolioId,
    event_category: 'feature_usage'
  });
};

export const trackRiskAssessmentCompleted = (riskProfile, score) => {
  trackEvent('risk_assessment_completed', {
    risk_profile: riskProfile,
    score,
    event_category: 'onboarding'
  });
};

// ==================== EDUCATION EVENTS ====================

export const trackEducationViewed = (articleId, articleTitle, isPremium = false) => {
  trackEvent('education_viewed', {
    article_id: articleId,
    article_title: articleTitle,
    is_premium: isPremium,
    event_category: 'content'
  });
};

export const trackBookDownloaded = (bookType) => {
  trackEvent('book_downloaded', {
    book_type: bookType, // 'summary' or 'full'
    event_category: 'content'
  });
};

// ==================== ALERT EVENTS ====================

export const trackAlertRead = (alertType) => {
  trackEvent('alert_read', {
    alert_type: alertType,
    event_category: 'engagement'
  });
};

export const trackAlertSettingsUpdated = () => {
  trackEvent('alert_settings_updated', {
    event_category: 'settings'
  });
};

// ==================== ERROR EVENTS ====================

export const trackError = (errorType, errorMessage, errorLocation = '') => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    error_location: errorLocation,
    event_category: 'errors'
  });
};

// ==================== PRICING PAGE SPECIFIC ====================

/**
 * Track when user views the pricing page
 */
export const trackViewPricingPage = () => {
  trackEvent('view_pricing_page', {
    event_category: 'ecommerce',
    event_label: 'Pricing page viewed'
  });
};

/**
 * Track pricing plan comparison
 */
export const trackComparePlans = () => {
  trackEvent('compare_plans', {
    event_category: 'ecommerce'
  });
};

/**
 * Track sector heatmap view (Premium feature)
 */
export const trackHeatmapViewed = (currentCycle) => {
  trackEvent('heatmap_viewed', {
    event_category: 'feature_usage',
    current_cycle: currentCycle
  });
};

export default {
  trackEvent,
  trackPageView,
  setUserProperties,
  identifyUser,
  trackSignUp,
  trackLogin,
  trackLogout,
  trackViewItem,
  trackAddToCart,
  trackBeginCheckout,
  trackPurchase,
  trackSubscriptionUpgrade,
  trackWaitlistSignup,
  trackCTAClicked,
  trackPortfolioCreated,
  trackPortfolioDeleted,
  trackBacktestRun,
  trackInstrumentEvaluated,
  trackGuidanceGenerated,
  trackRebalanceViewed,
  trackRiskAssessmentCompleted,
  trackEducationViewed,
  trackBookDownloaded,
  trackAlertRead,
  trackAlertSettingsUpdated,
  trackError,
  trackViewPricingPage,
  trackComparePlans,
  trackHeatmapViewed,
};
